import type {NodeLocationInfo} from 'Contexts/NodeCallBackContext';
import {useAnimationFrame} from 'Hooks/useAnimationFrame';
import React, {useCallback, useRef} from 'react';
import {AstView, EnrichedAstNode} from 'rendition-core';
import {getEdgesAndOffsets} from 'utils/getEdgesAndOffsets';
import {AbsoluteUnitsSelect} from './AbsoluteUnitsSelect';

interface Props {
    isLeftHandle?: boolean;
    itemHeight: number;
    itemWidth: number;
    relativeParentWidth: number;
    xy: () => NodeLocationInfo['actual'];
    locationInfo: () => NodeLocationInfo;
    rootLocationInfo: () => NodeLocationInfo;
    node: EnrichedAstNode;
    view: AstView;
    setView: (view: AstView) => void;
}

const DEFAULT_HEIGHT = 40;
const BORDER_WIDTH = 2;

export const HorizontalHandle: React.FC<Props> = ({
    itemHeight,
    itemWidth,
    isLeftHandle,
    relativeParentWidth,
    xy,
    locationInfo,
    rootLocationInfo,
    node,
    view,
    setView,
    ...props
}) => {
    const ref = React.useRef<null | HTMLDivElement>(null);
    const unitRef = React.useRef<null | HTMLDivElement>(null);

    const width = useRef(0);

    const minHeight = itemHeight < 50 && itemHeight > 20 ? itemHeight - 10 : 10;
    const height = itemHeight < 50 ? minHeight : DEFAULT_HEIGHT;

    const styles: React.CSSProperties = {
        position: 'absolute',
        transform: 'translateY(-50%)',
        height,
        zIndex: 11,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };

    const isRight = node.inputLayoutAbstraction.horizontalAnchor === 'right';
    const isLeft = !isRight;

    const updateOnFrame = useCallback(() => {
        if (ref.current && unitRef.current) {
            // callback

            const edgesAndOffsets = getEdgesAndOffsets(xy, locationInfo, rootLocationInfo);

            const leftNegative = edgesAndOffsets.leftEdgeOfSelectedNode < 0;
            const rightNegative = edgesAndOffsets.spaceBetweenRightEdge < 0;

            width.current = isLeftHandle
                ? Math.abs(edgesAndOffsets.leftEdgeOfSelectedNode) - (leftNegative ? 0 : BORDER_WIDTH)
                : Math.abs(edgesAndOffsets.spaceBetweenRightEdge) - (rightNegative ? 0 : BORDER_WIDTH);

            const styles = ref.current.style;
            const unitStyles = unitRef.current.style;

            styles.left = '';
            styles.right = '';
            unitStyles.left = '';
            unitStyles.right = '';
            const unitBoxSize = unitRef.current.getBoundingClientRect();
            const unitSelectWidth = unitBoxSize.width + 5;

            styles.width = width.current < BORDER_WIDTH ? '0px' : `${Math.abs(width.current)}px`;

            styles.top = edgesAndOffsets.topEdgeOfSelectedNode + edgesAndOffsets.offsetTop + itemHeight / 2 + 'px';

            if (isLeftHandle) {
                styles.left = edgesAndOffsets.offsetLeft + 'px';
            } else {
                styles.right = edgesAndOffsets.offsetRight + 'px';
            }

            if (leftNegative && isLeftHandle) {
                styles.left = edgesAndOffsets.leftEdgeOfSelectedNode + edgesAndOffsets.offsetLeft + 'px';
                styles.right = relativeParentWidth + 'px';
            } else if (rightNegative && !isLeftHandle) {
                styles.right = edgesAndOffsets.spaceBetweenRightEdge + edgesAndOffsets.offsetRight + 'px';
            }

            unitStyles.top =
                edgesAndOffsets.topEdgeOfSelectedNode + edgesAndOffsets.offsetTop + itemHeight / 2 - 15 + 'px';

            const unitsLeft =
                edgesAndOffsets.leftEdgeOfSelectedNode < 0
                    ? edgesAndOffsets.leftEdgeOfSelectedNode + edgesAndOffsets.offsetLeft - unitSelectWidth
                    : edgesAndOffsets.offsetLeft - unitSelectWidth;
            const unitsRight =
                edgesAndOffsets.spaceBetweenRightEdge < 0
                    ? edgesAndOffsets.spaceBetweenRightEdge +
                      edgesAndOffsets.offsetRight -
                      unitSelectWidth -
                      BORDER_WIDTH
                    : edgesAndOffsets.offsetRight - unitSelectWidth;
            unitStyles[isLeft ? 'left' : 'right'] = (isLeft ? unitsLeft : unitsRight) + 'px';
        }
    }, [isLeft, isLeftHandle, itemHeight, locationInfo, relativeParentWidth, rootLocationInfo, xy]);

    useAnimationFrame(updateOnFrame);

    return (
        <>
            <div ref={ref} style={styles}>
                <svg width="100%" height="100%" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
                    <line
                        x1="0"
                        x2="100%"
                        y1="50%"
                        y2="50%"
                        strokeLinecap="round"
                        strokeDasharray="0.5 4"
                        stroke="#9688EE"
                        strokeWidth={2}
                    />
                </svg>

                <svg
                    style={{[isLeft ? 'right' : 'left']: -6, position: 'absolute'}}
                    width="10"
                    height="10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    {...props}
                >
                    <circle cy="5" cx="5" r={4.5} fill="#7B74FF" stroke="black" />
                </svg>
            </div>
            <AbsoluteUnitsSelect
                ref={unitRef}
                anchor={isLeft ? 'left' : 'right'}
                node={node}
                view={view}
                setView={setView}
            />
        </>
    );
};
